/** Notification editor (/notifications/:id) — the console's full editing
 *  surface for one notification: identity + language policy, composition
 *  (block refs), per-delivery-method template editing with backend validation,
 *  change history with client-side restore, draft-aware test rendering and a
 *  context-schema side panel.
 *
 *  State seam: `useNotificationDraft` (lifted draft + dirtyPaths → sparse
 *  PATCH). Saves go through SaveCommentDialog (audit comment) with an
 *  optimistic-concurrency token; 400s are distributed onto the editor
 *  surfaces via mapNotificationErrors, 409s raise the ConflictBanner.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PolylineIcon from '@mui/icons-material/Polyline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { asManagedRefusal, type ManagedAspectKey, type ManagedRefusal } from '@/api/cannedNotifications';
import { apiDelete, ConsoleApiError } from '@/api/client';
import * as notificationsApi from '@/api/notifications';
import type { NotificationWrite } from '@/api/notifications';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConflictBanner, EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { cond, deliveryLogsHref, notificationsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { labelWithHelp } from '@/components/FieldHelp';
import type {
  BlockCompletionItem,
  ContextSchemaSource,
  TemplateCodeEditorHandle,
  TemplateDiagnostic,
} from '@/components/TemplateCodeEditor';
import { usePermissions } from '@/hooks/usePermissions';

import { NOTIFICATION_HELP } from './fieldHelp';
import { ContextSchemaDrawer } from './ContextSchemaDrawer';
import { useEffectiveBlocks } from './composition/ResolvedBlocksPreview';
import { ALL_TEMPLATE_FIELDS } from './delivery/channelFields';
import { anatomyUrl } from '../notification-anatomy/anatomyLink';
import { deliveryLogsUrl } from './deliveryLogsLink';
import { DeliveryMethodEditor } from './delivery/DeliveryMethodEditor';
import {
  DeliveryMethodList,
  methodLabel,
  type DmValidationStatus,
} from './delivery/DeliveryMethodList';
import { AdvancedRulesSection } from './AdvancedRulesSection';
import { AutoReplyPanel, autoRepliesInvalid } from './AutoReplyPanel';
import { CannedManagedPanel } from './CannedManagedPanel';
import { ManagedRefusalBanner } from './ManagedRefusalBanner';
import { NotificationCompositionPanel } from './NotificationCompositionPanel';
import { NotificationHistoryDialog } from './NotificationHistoryDialog';
import { NotificationRulePanel } from './NotificationRulePanel';
import { RulesEditor } from './rules/RulesEditor';
import { useRuleIssues } from './rules/useRuleIssues';
import { useNotificationEditErrors } from './notificationEditErrors';
import { SaveCommentDialog } from './SaveCommentDialog';
import { TestRenderDialog } from './test-render/TestRenderDialog';
import { useStickyRenderContext } from './test-render/useStickyRenderContext';
import { useNotificationDraft } from './useNotificationDraft';
import { useRevealIntent } from './useRevealIntent';

const MANAGE_PERM = 'console_notifications_manage';

export function NotificationEditPage() {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const validId = Number.isFinite(id);
  const navigate = useNavigate();

  // Guard non-numeric ids (e.g. a stray /notifications/foo deep link).
  useEffect(() => {
    if (!validId) navigate('/notifications', { replace: true });
  }, [validId, navigate]);

  if (!validId) return null;
  // Key by id: in-place navigation (duplicate → the new record) must reset the
  // whole editor state — the draft hook initializes once per mount by design.
  return <NotificationEditor key={id} id={id} />;
}

function NotificationEditor({ id }: { id: number }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPerm } = usePermissions();
  const canManage = hasPerm(MANAGE_PERM);

  const detailQuery = useQuery({
    queryKey: ['notification', id],
    queryFn: () => notificationsApi.retrieve(id),
  });
  const detail = detailQuery.data;
  const draft = useNotificationDraft(detail);
  // Client-side rule-args validation (same gating as the server: new/changed rows block).
  const ruleIssues = useRuleIssues(draft.draft.rules, detail?.rules);
  const {
    setMeta,
    setDmField,
    setDeliveryMethods,
    addDeliveryMethod,
    removeDeliveryMethod,
    setBlockRefs,
    setRules,
    setAdvancedRules,
    setAutoReplies,
  } = draft;

  // ---- editor-side state ---------------------------------------------------
  const errors = useNotificationEditErrors();
  const [selectedDmId, setSelectedDmId] = useState<number | null>(null);

  // `?dm=&field=&line=` — opens this editor at a specific template line, which
  // is what makes a line number in the Anatomy viewer navigable.
  const reveal = useRevealIntent(setSelectedDmId);
  // A link that cannot be resolved (stale delivery-method id, a field this
  // channel does not have) must say so — silence reads as a broken feature.
  useEffect(() => {
    if (reveal.unresolved) {
      snackbar.show('That link points at a template field this notification does not have.', 'warning');
    }
  }, [reveal.unresolved, snackbar]);
  const [conflict, setConflict] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [defaultComment, setDefaultComment] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [testRender, setTestRender] = useState<{ open: boolean; dmId?: number }>({ open: false });
  // Sticky per-notification render context, shared by the test-render dialog
  // and the docked live preview (pick a booking once, use it everywhere).
  const [renderContext, setRenderContext] = useStickyRenderContext(id);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Post-delete navigation happens in an effect AFTER the navigation guard
  // re-registered with `when: false` — otherwise a dirty draft would prompt
  // "unsaved changes" while leaving a record that no longer exists.
  const [deleted, setDeleted] = useState(false);
  useEffect(() => {
    if (deleted) navigate('/notifications');
  }, [deleted, navigate]);

  /** The last-focused template editor — the schema drawer inserts into it. */
  const focusedEditorRef = useRef<TemplateCodeEditorHandle | null>(null);

  // Select the first delivery method once the record arrives.
  const deliveryMethods = draft.draft.deliveryMethods;
  useEffect(() => {
    if (selectedDmId == null && deliveryMethods.length > 0) {
      setSelectedDmId(deliveryMethods[0]?.id ?? null);
    }
  }, [selectedDmId, deliveryMethods]);
  const selectedDm = deliveryMethods.find((dm) => dm.id === selectedDmId);

  // ---- shared lookups ------------------------------------------------------
  const event = detail?.notification.event ?? '';
  const tenantId: number | 'system' = detail?.notification.tour_operator ?? 'system';

  const schemaQuery = useQuery({
    queryKey: ['nf-event-schema', event],
    queryFn: ({ signal }) => notificationsApi.getEventSchema(event, signal),
    staleTime: 30 * 60_000,
    enabled: Boolean(event),
  });
  const contextSchemas = useMemo<ContextSchemaSource[]>(
    () =>
      schemaQuery.data
        ? [
            {
              id: event,
              ...(detail?.refs.event_label ? { label: detail.refs.event_label } : {}),
              schema: schemaQuery.data,
            },
          ]
        : [],
    [schemaQuery.data, event, detail?.refs.event_label],
  );

  const metadataQuery = useQuery({
    queryKey: ['nf-editor-metadata', String(tenantId)],
    queryFn: () => notificationsApi.getEditorMetadata(tenantId),
    staleTime: 5 * 60_000,
    enabled: Boolean(detail),
  });

  const effectiveBlocks = useEffectiveBlocks(draft.draft.blockRefs);

  // Editor-metadata blocks + the notification's own ref aliases (an alias not
  // in the catalog must still complete as `block.<alias>`).
  const blockCompletions = useMemo<BlockCompletionItem[]>(() => {
    const out = new Map<string, BlockCompletionItem>();
    for (const b of metadataQuery.data?.blocks ?? []) {
      out.set(b.codename, {
        codename: b.codename,
        name: b.name,
        description: b.description,
        type: b.type,
      });
    }
    for (const ref of draft.draft.blockRefs) {
      if (ref.alias && !out.has(ref.alias)) {
        out.set(ref.alias, {
          codename: ref.alias,
          name: `alias of ${ref.block_codename ?? ref.block_list_codename ?? '?'}`,
        });
      }
    }
    return [...out.values()];
  }, [metadataQuery.data?.blocks, draft.draft.blockRefs]);

  // ---- draft edit wrappers -------------------------------------------------
  const handleDmFieldChange = useCallback(
    (dmId: number, field: string, value: unknown) => {
      setDmField(dmId, field, value);
      if (ALL_TEMPLATE_FIELDS.includes(field)) errors.markFieldStale(dmId, field);
    },
    [setDmField, errors],
  );

  const handleValidationResult = useCallback(
    (dmId: number, field: string, ok: boolean, diags: TemplateDiagnostic[]) => {
      errors.setFieldValidation(dmId, field, ok, diags);
    },
    [errors],
  );

  const handleAddMethod = useCallback(
    (method: number) => {
      const newId = addDeliveryMethod(method);
      setSelectedDmId(newId);
    },
    [addDeliveryMethod],
  );

  const handleRemoveMethod = useCallback(
    (dmId: number) => {
      removeDeliveryMethod(dmId);
      // The first remaining method gets selected by the effect above.
      setSelectedDmId((current) => (current === dmId ? null : current));
    },
    [removeDeliveryMethod],
  );

  const openContextPanel = useCallback(() => setDrawerOpen(true), []);

  const dmStatus = useMemo(() => {
    const out: Record<number, DmValidationStatus> = {};
    for (const dm of deliveryMethods) {
      const prefix = `${dm.id}.`;
      const states = Object.entries(errors.validation)
        .filter(([key]) => key.startsWith(prefix))
        .map(([, state]) => state);
      out[dm.id] = states.includes('invalid')
        ? 'invalid'
        : states.includes('stale')
          ? 'stale'
          : states.includes('ok')
            ? 'ok'
            : 'none';
    }
    return out;
  }, [deliveryMethods, errors.validation]);

  const hasInvalid = Object.values(errors.validation).includes('invalid');
  const [showAutoReplyValidation, setShowAutoReplyValidation] = useState(false);

  // ---- mutations -----------------------------------------------------------
  const [managedRefusal, setManagedRefusal] = useState<ManagedRefusal | null>(null);

  const saveMutation = useMutation({
    mutationFn: (comment: string) => {
      const extra: Pick<NotificationWrite, 'comment' | 'expected_updated_ts'> = {};
      if (comment) extra.comment = comment;
      if (detail?.updated_ts) extra.expected_updated_ts = detail.updated_ts;
      return notificationsApi.update(id, draft.buildPatch(extra));
    },
    onSuccess: (result) => {
      setSaveDialogOpen(false);
      setDefaultComment('');
      setManagedRefusal(null);
      errors.clearAll();
      setConflict(false);
      draft.reset(result);
      qc.setQueryData(['notification', id], result);
      void qc.invalidateQueries({ queryKey: ['notification', id] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['notification-changes', id] });
      snackbar.show('Notification saved.', 'success');
    },
    onError: (e) => {
      const refusal = e instanceof ConsoleApiError ? asManagedRefusal(e.body) : null;
      if (refusal) {
        // Not a validation failure: the edit is fine, the notification is
        // simply not the editor's to change yet. Handled before the generic
        // 400 path, which would file it under "check the highlighted fields"
        // and highlight nothing.
        setSaveDialogOpen(false);
        setManagedRefusal(refusal);
        return;
      }
      if (e instanceof ConsoleApiError && e.status === 400) {
        setSaveDialogOpen(false);
        const { firstDmId, general } = errors.applyServerErrors(e.body);
        // Auto-select the first offending delivery method so its red
        // diagnostics are visible immediately.
        if (firstDmId != null) setSelectedDmId(firstDmId);
        snackbar.show(
          general.length > 0
            ? general.join(' ')
            : 'The save was rejected — check the highlighted fields.',
          'error',
        );
        return;
      }
      if (e instanceof ConsoleApiError && e.status === 409) {
        setSaveDialogOpen(false);
        setConflict(true);
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete<void>(`/notifications/${id}/`),
    onSuccess: () => {
      snackbar.show('Notification deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      setDeleted(true);
    },
    onError: (e) => {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => notificationsApi.duplicate(id),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      snackbar.show(`Duplicated as notification #${result.id}.`, 'success');
      navigate(`/notifications/${result.id}`);
    },
    onError: (e) => {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Duplicate failed.', 'error');
    },
  });

  const handleConflictReload = useCallback(() => {
    void detailQuery.refetch().then((res) => {
      if (res.data) {
        draft.reset(res.data);
        errors.clearAll();
        setConflict(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQuery.refetch, draft.reset, errors.clearAll]);

  const handleDiscard = useCallback(() => {
    if (!detail) return;
    draft.reset(detail);
    errors.clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, draft.reset, errors.clearAll]);

  const handleSaveClick = () => {
    if (hasInvalid) {
      snackbar.show('Fix the invalid templates (red) before saving.', 'error');
      return;
    }
    if (ruleIssues.blocking) {
      snackbar.show('Fix the rules marked in red before saving.', 'error');
      return;
    }
    if (draft.dirtyPaths.has('auto_replies') && autoRepliesInvalid(draft.draft.autoReplies)) {
      setShowAutoReplyValidation(true);
      snackbar.show('Every auto-reply option needs an answer (or AI prompt) before saving.', 'error');
      return;
    }
    setSaveDialogOpen(true);
  };

  const copyDashboardPath = () => {
    if (!detail) return;
    void navigator.clipboard.writeText(detail.refs.dashboard_link).then(
      () => snackbar.show('Dashboard path copied.', 'success'),
      () => snackbar.show('Copy failed.', 'error'),
    );
  };

  // ---- render --------------------------------------------------------------
  if (detailQuery.isLoading || !detail) {
    if (detailQuery.isError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Couldn’t load notification #{id}.
        </Alert>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const title = draft.draft.meta.name || `Notification #${detail.id}`;
  const tenantLabel = detail.refs.tenant
    ? `${detail.refs.tenant.code} — ${detail.refs.tenant.name}`
    : 'System (no tenant)';
  const eventCode = detail.notification.event;
  const eventLabel = `${eventCode}${detail.refs.event_label ? ` — ${detail.refs.event_label}` : ''}`;
  const tenantCond = detail.refs.tenant ? [cond('tenant', detail.refs.tenant.code)] : [];
  const subtitle = (
    <DetailSubtitle
      parts={[
        detail.refs.tenant ? (
          <ValueActions
            actions={operatorActions(detail.refs.tenant, {
              href: notificationsHref(tenantCond),
              label: 'Notifications of this operator',
            })}
          >
            {tenantLabel}
          </ValueActions>
        ) : (
          tenantLabel
        ),
        <ValueActions
          key="event"
          actions={[
            act.copy(eventCode, 'Copy event code'),
            act.filter(notificationsHref([...tenantCond, cond('event', eventCode)]), 'Notifications for this event'),
            act.filter(
              deliveryLogsHref([cond('event', eventCode)], { tenant: detail.refs.tenant?.id }),
              'Delivery logs for this event (7 days)',
            ),
          ]}
        >
          {eventLabel}
        </ValueActions>,
        <ValueActions key="id" actions={[act.copy(String(detail.id), 'Copy id')]}>ID {detail.id}</ValueActions>,
      ]}
    />
  );

  return (
    <Box>
      <PageHeader
        helpStory="notification-grid-editor"
        title={title}
        subtitle={subtitle}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Path to this notification in the operator dashboard (the dashboard host depends on the operator) — click to copy.">
              <Chip
                size="small"
                variant="outlined"
                icon={<ContentCopyIcon />}
                label={detail.refs.dashboard_link}
                onClick={copyDashboardPath}
                sx={{ maxWidth: 220 }}
              />
            </Tooltip>
            <PermissionGate perm={MANAGE_PERM}>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={Boolean(draft.draft.meta.enabled)}
                    onChange={(e) => setMeta('enabled', e.target.checked)}
                  />
                }
                label={labelWithHelp(
                  <Typography variant="body2">Enabled</Typography>,
                  NOTIFICATION_HELP.enabled,
                )}
              />
            </PermissionGate>
            <Button
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={() => setTestRender({ open: true })}
            >
              Test render
            </Button>
            <Button size="small" startIcon={<HistoryIcon />} onClick={() => setHistoryOpen(true)}>
              History
            </Button>
            <Tooltip title="Open the delivery logs for this notification (last 7 days) in a new tab.">
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={deliveryLogsUrl(detail.id)}
                target="_blank"
                rel="noopener"
              >
                Delivery logs
              </Button>
            </Tooltip>
            <Tooltip
              describeChild
              title="Open this notification's structure — layout, blocks and every template's branches — in a new tab."
            >
              <Button
                size="small"
                startIcon={<PolylineIcon />}
                component="a"
                href={anatomyUrl(detail.id, detail.notification.tour_operator)}
                target="_blank"
                rel="noopener"
              >
                Anatomy
              </Button>
            </Tooltip>
            <Tooltip title="Context variables & blocks panel">
              <IconButton
                size="small"
                aria-label="Toggle the context panel"
                color={drawerOpen ? 'primary' : 'default'}
                onClick={() => setDrawerOpen((o) => !o)}
              >
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <PermissionGate perm={MANAGE_PERM}>
              <IconButton
                size="small"
                aria-label="More actions"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </PermissionGate>
          </Stack>
        }
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          disabled={duplicateMutation.isPending}
          onClick={() => {
            setMenuAnchor(null);
            duplicateMutation.mutate();
          }}
        >
          <ListItemIcon>
            <LibraryAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setConfirmDelete(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ConflictBanner
            open={conflict}
            onReload={handleConflictReload}
            message="This notification changed on the server (another editor). Reload before saving — your unsaved edits will be discarded."
          />

          {managedRefusal && (
            <ManagedRefusalBanner
              refusal={managedRefusal}
              canManage={canManage}
              onDismiss={() => setManagedRefusal(null)}
              onUnmanage={(aspects: ManagedAspectKey[]) => {
                const body = Object.fromEntries(aspects.map((a) => [a, false]));
                void notificationsApi.setManaged(id, body).then((result) => {
                  setManagedRefusal(null);
                  // Keep the draft: the edit that was refused is still in the
                  // form, and the point of unmanaging is to be able to save it.
                  qc.setQueryData(['notification', id], result);
                  void qc.invalidateQueries({ queryKey: ['notification', id] });
                  snackbar.show('Unmanaged — save again to keep your edit.', 'success');
                });
              }}
            />
          )}

          <Stack spacing={2}>
            {detail.refs.canned && (
              <CannedManagedPanel
                notificationId={detail.id}
                canned={detail.refs.canned}
                core={detail.notification}
                canManage={canManage}
                canRefresh={hasPerm('console_canned_notifications_materialise')}
                dirty={draft.dirty}
                onManagedChanged={(fresh) => {
                  // A flag flip does not touch anything the editor holds, so
                  // the draft stays — resetting it here would discard unsaved
                  // work as a side effect of toggling a switch.
                  qc.setQueryData(['notification', id], fresh);
                }}
                onRefreshed={handleConflictReload}
              />
            )}

            <NotificationRulePanel
              detail={detail}
              meta={draft.draft.meta}
              setMeta={setMeta}
              errors={errors.meta}
              canManage={canManage}
              tenantId={tenantId}
            />

            <Paper variant="outlined" sx={{ p: 2 }}>
              <RulesEditor
                value={draft.draft.rules}
                onChange={(rules) => {
                  setRules(rules);
                  errors.clearRules(); // server row errors are keyed by index — stale after an edit
                }}
                matchType={Number(draft.draft.meta.rules_match_type ?? 0)}
                onMatchTypeChange={(v) => setMeta('rules_match_type', v)}
                tenantId={tenantId}
                errors={{ ...ruleIssues.errors, ...errors.rules }}
                warnings={ruleIssues.warnings}
                disabled={!canManage}
                emptyMatchesLabel="every occurrence of the event"
                contextSchemas={contextSchemas}
              />
            </Paper>

            <NotificationCompositionPanel
              detail={detail}
              blockRefs={draft.draft.blockRefs}
              disableDefaultBlocks={Boolean(draft.draft.meta.disable_default_blocks)}
              onToggleDefaultBlocks={(v) => setMeta('disable_default_blocks', v)}
              onBlockRefsChange={setBlockRefs}
              errors={errors.blockRefs}
              canManage={canManage}
              onOpenTestRender={() => setTestRender({ open: true })}
            />

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                Delivery methods
              </Typography>
              {mdDown ? (
                <Stack spacing={1.5}>
                  <Select
                    size="small"
                    fullWidth
                    value={selectedDmId ?? ''}
                    onChange={(e) => setSelectedDmId(Number(e.target.value))}
                    displayEmpty
                  >
                    {deliveryMethods.map((dm) => (
                      <MenuItem key={dm.id} value={dm.id}>
                        {methodLabel(dm.method)} {dm.id > 0 ? `#${dm.id}` : '(new)'}
                        {dm.enabled ? '' : ' (disabled)'}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedDm && (
                    <DeliveryMethodEditor
                      dm={selectedDm}
                      detail={detail}
                      draft={draft}
                      renderContext={renderContext}
                      onRenderContextChange={setRenderContext}
                      canManage={canManage}
                      themes={metadataQuery.data?.themes}
                      blocks={blockCompletions}
                      contextSchemas={contextSchemas}
                      blockRefCount={draft.draft.blockRefs.length}
                      diagnostics={errors.dmDiagnostics[selectedDm.id]}
                      validation={errors.validation}
                      onFieldChange={handleDmFieldChange}
                      onValidationResult={handleValidationResult}
                      onOpenTestRender={(dmId) => setTestRender({ open: true, dmId })}
                      onOpenContextPanel={openContextPanel}
                      focusRegistry={focusedEditorRef}
                      registerEditor={reveal.register}
                      revealField={reveal.intent?.dmId === selectedDm.id ? reveal.intent.field : null}
                    />
                  )}
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 300, flexShrink: 0 }}>
                    <DeliveryMethodList
                      items={deliveryMethods}
                      themes={detail.refs.themes}
                      selectedId={selectedDmId}
                      statusById={dmStatus}
                      canManage={canManage}
                      onSelect={setSelectedDmId}
                      onToggleEnabled={(dmId, enabled) => setDmField(dmId, 'enabled', enabled)}
                      onReorder={(ordered) => setDeliveryMethods(ordered)}
                      onAdd={handleAddMethod}
                      onRemove={handleRemoveMethod}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {selectedDm ? (
                      <DeliveryMethodEditor
                        dm={selectedDm}
                        detail={detail}
                        draft={draft}
                        renderContext={renderContext}
                        onRenderContextChange={setRenderContext}
                        canManage={canManage}
                        themes={metadataQuery.data?.themes}
                        blocks={blockCompletions}
                        contextSchemas={contextSchemas}
                        blockRefCount={draft.draft.blockRefs.length}
                        diagnostics={errors.dmDiagnostics[selectedDm.id]}
                        validation={errors.validation}
                        onFieldChange={handleDmFieldChange}
                        onValidationResult={handleValidationResult}
                        onOpenTestRender={(dmId) => setTestRender({ open: true, dmId })}
                        onOpenContextPanel={openContextPanel}
                        focusRegistry={focusedEditorRef}
                        registerEditor={reveal.register}
                        revealField={
                          reveal.intent?.dmId === selectedDm.id ? reveal.intent.field : null
                        }
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        Select a delivery method on the left.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              <AdvancedRulesSection
                deliveryMethods={deliveryMethods}
                ordering={Number(draft.draft.meta.delivery_method_ordering ?? 0)}
                onOrderingChange={(v) => setMeta('delivery_method_ordering', v)}
                rules={draft.draft.advancedRules}
                onRulesChange={setAdvancedRules}
                errors={errors.advancedRules}
                canManage={canManage}
              />
            </Paper>

            <AutoReplyPanel
              value={draft.draft.autoReplies}
              onChange={setAutoReplies}
              tenantId={tenantId}
              canManage={canManage}
              errors={errors.autoReplies}
              showValidation={showAutoReplyValidation}
            />
          </Stack>

          <EditorSaveBar
            dirty={draft.dirty}
            saving={saveMutation.isPending}
            canManage={canManage}
            onSave={handleSaveClick}
            onDiscard={handleDiscard}
            guard={!deleted}
            guardMessage="You have unsaved changes on this notification. Leaving now will discard them."
          />
        </Box>

        <ContextSchemaDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          event={detail.notification.event}
          eventLabel={detail.refs.event_label}
          tenantId={tenantId}
          effectiveBlocks={effectiveBlocks.rows}
          insertRegistry={focusedEditorRef}
        />
      </Box>

      <SaveCommentDialog
        open={saveDialogOpen}
        changedPaths={[...draft.dirtyPaths]}
        deliveryMethods={deliveryMethods}
        defaultComment={defaultComment}
        saving={saveMutation.isPending}
        onCancel={() => setSaveDialogOpen(false)}
        onSave={(comment) => saveMutation.mutate(comment)}
      />

      {historyOpen && (
        <NotificationHistoryDialog
          open
          notificationId={detail.id}
          draft={draft}
          onClose={() => setHistoryOpen(false)}
          onRestored={(comment) => {
            setDefaultComment(comment);
            snackbar.show('Snapshot loaded into the editor — review and save to apply.', 'success');
          }}
        />
      )}

      {testRender.open && (
        <TestRenderDialog
          open
          onClose={() => setTestRender({ open: false })}
          detail={detail}
          draft={draft}
          source={renderContext}
          onSourceChange={setRenderContext}
          deliveryMethodId={testRender.dmId}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this notification?"
        message={`"${title}" and its delivery methods will be deleted. The change history keeps a "deleted" record with the final snapshot.`}
        confirmLabel="Delete notification"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
